import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import usePermissions from "hooks/usePermissions";
import { ReactComponent as InvoiceIcon } from "assets/images/iconsV2/receipt-check.svg";
import { ReactComponent as PackageIcon } from "assets/images/iconsV2/package.svg";
import { ReactComponent as QuoteIcon } from "assets/images/iconsV2/quote-request.svg";
import { ReactComponent as TruckIcon } from "assets/images/iconsV2/truck.svg";
import { getStatusRoute, mapGroupNameToTranslationKey, mapStatusToTranslationKey } from "../StatusWidgetUtils";
import "./StatusItem.scss";

interface StatusItemProps {
  title: string;
  count: number;
  status: string;
  lastItem: boolean;
  displayTitle: boolean;
}

type IconComponentType = React.FunctionComponent<React.SVGProps<SVGSVGElement>>;

const iconMap: Record<string, IconComponentType> = {
  Orders: PackageIcon,
  "Quote request": QuoteIcon,
  Shipments: TruckIcon,
  Invoices: InvoiceIcon,
};

const renderIcon = (title: string): JSX.Element | null => {
  const IconComponent = iconMap[title];
  return IconComponent ? (
    <IconComponent style={{ width: "16px", height: "16px" }} className="status-item-container__header__icon" />
  ) : null;
};

const highlightedStatuses = [
  "Pending approval",
  "Pending quotation",
  "Pending booking",
  "Pending revalidation",
  "Pending audit",
  "Pending submission",
  "Declined",
  "Delayed",
  "Incomplete",
];

function StatusItem({ title, count, status, lastItem, displayTitle }: StatusItemProps) {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");

  const handleStatusClick = useCallback(() => {
    const route = getStatusRoute(status, user_type);
    if (route) navigate(route);
  }, [status, navigate, user_type]);

  const formattedStatus = user_type === "shipper" && status === "Quoted" ? "Pending booking" : status;
  const translatedStatus = mapStatusToTranslationKey(formattedStatus);
  const isHighlighted = highlightedStatuses.includes(formattedStatus);

  return (
    <div className="d-flex">
      <div className={`status-item-container ${lastItem ? "status-item-container__last" : ""}`}>
        <div className="status-item-container__header">
          {displayTitle && (
            <>
              {renderIcon(title)}
              <div className="text-sm text-primary font-medium no-wrap">{mapGroupNameToTranslationKey(title)}</div>
            </>
          )}
        </div>
        <div
          role="button"
          tabIndex={0}
          className="status-item-container__content"
          onClick={handleStatusClick}
          onKeyDown={handleStatusClick}
        >
          <div className={`${isHighlighted ? "text-warning" : "text-brand-tertiary"} display-xs font-semibold`}>{count}</div>
          <div className="text-xs font-medium text-secondary-v2 no-wrap">{translatedStatus}</div>
        </div>
      </div>
      {lastItem && (
        <svg xmlns="http://www.w3.org/2000/svg" width="2" height="78" viewBox="0 0 2 78" fill="none">
          <path d="M1.25 0C1.25 49.92 1.25 72.8 1.25 78" stroke="#E2E8F0" />
        </svg>
      )}
    </div>
  );
}

export default StatusItem;
